import { useCallback, useEffect, useState } from "react";
import { Button, Card, Checkbox, Col, Divider, Form, Input, Modal, Row, Space, Typography } from "antd";
import { SaveOutlined, UndoOutlined } from '@ant-design/icons';
import {
  apiGetSystemConfig,
  apiUpdateSystemConfig,
  apiGetFeatureFlag,
  apiUpdateFeatureFlag,
} from "../../api";
import { SystemConfig } from "../../interfaces/system-config.interface";
import { toastError, toastSuccess } from "../../utils/toast";
import { useLoading } from "../../context/LoadingContext";

const { Title } = Typography;

const CONFIG_DISPLAY_NAMES: Record<string, string> = {
  'NotifyTypeTransactionFail': 'Thông báo giao dịch thất bại',
  'NotifyTypeTransactionSuccess': 'Thông báo giao dịch thành công',
  'NotifyTypeTransactionUnknownResult': 'Thông báo giao dịch kết quả không xác định',
  'NotifyTypeTransactionCheckAndRetry': 'Thông báo kiểm tra và thử lại giao dịch',
  'NotifyTypeSendNotify': 'Gửi thông báo',
  'NotifyTypeSendNotifySlack': 'Gửi thông báo Slack',
};

const FEATURE_FLAG_DISPLAY_NAMES: Record<string, string> = {
  'IsEnableSelectProviderByNetwork': 'Chọn nhà cung cấp theo network của đối tác',
};

const getConfigDisplayName = (key: string): string => CONFIG_DISPLAY_NAMES[key] || key;
const getFeatureFlagDisplayName = (key: string): string => FEATURE_FLAG_DISPLAY_NAMES[key] || key;

const isBooleanValue = (value: string): boolean => value === 'true' || value === 'false';
const parseBooleanValue = (value: string): boolean => value === 'true';

export default function SystemConfigurationPage() {
  const [form] = Form.useForm();
  const { setIsLoading } = useLoading();

  const [systemConfigs, setSystemConfigs] = useState<SystemConfig[]>([]);
  const [featureFlags, setFeatureFlags] = useState<SystemConfig[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [otpModalVisible, setOtpModalVisible] = useState(false);
  const [otpValue, setOtpValue] = useState('');

  /**
   * Notify configs are stored with "IsDisabledX" semantics — flip on load so
   * the form displays the "enabled" state to the user.
   */
  const fetchAll = useCallback(async () => {
    try {
      setIsLoading(true);
      const [systemRes, featureRes] = await Promise.all([
        apiGetSystemConfig(),
        apiGetFeatureFlag(),
      ]);

      const systemList: SystemConfig[] = systemRes?.data?.systemConfigsList ?? [];
      for (const config of systemList) {
        config.value = String(!parseBooleanValue(config.value));
      }
      setSystemConfigs(systemList);

      const featureList: SystemConfig[] = featureRes?.data?.featureFlagsList ?? [];
      setFeatureFlags(featureList);

      const initialValues: Record<string, any> = {};
      systemList.forEach((c) => {
        initialValues[c.key] = isBooleanValue(c.value) ? parseBooleanValue(c.value) : c.value;
      });
      featureList.forEach((c) => {
        initialValues[c.key] = isBooleanValue(c.value) ? parseBooleanValue(c.value) : c.value;
      });
      form.setFieldsValue(initialValues);
    } catch (error) {
      console.error('Error fetching system config / feature flags:', error);
      setSystemConfigs([]);
      setFeatureFlags([]);
      form.setFieldsValue({});
      toastError('Không thể tải cấu hình từ server');
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, form]);

  const handleFormChange = () => {
    const formValues = form.getFieldsValue();
    const diff = (config: SystemConfig) => {
      const original = isBooleanValue(config.value) ? parseBooleanValue(config.value) : config.value;
      return formValues[config.key] !== original;
    };
    setHasChanges(systemConfigs.some(diff) || featureFlags.some(diff));
  };

  const handleCancelEdit = () => {
    if (!hasChanges) return;
    Modal.confirm({
      title: 'Xác nhận hủy',
      content: 'Bạn có chắc chắn muốn hủy? Tất cả thay đổi sẽ bị mất.',
      okText: 'Đồng ý',
      cancelText: 'Không',
      onOk: () => {
        const initialValues: Record<string, any> = {};
        [...systemConfigs, ...featureFlags].forEach((c) => {
          initialValues[c.key] = isBooleanValue(c.value) ? parseBooleanValue(c.value) : c.value;
        });
        form.setFieldsValue(initialValues);
        setHasChanges(false);
      },
    });
  };

  const handleSaveClick = async () => {
    try {
      await form.validateFields();
      setOtpModalVisible(true);
    } catch (error) {
      toastError('Vui lòng kiểm tra lại các trường thông tin');
    }
  };

  const handleSubmitUpdate = async () => {
    if (!otpValue.trim()) {
      toastError('Vui lòng nhập mã OTP xác nhận');
      return;
    }

    try {
      setIsLoading(true);
      const formValues = form.getFieldsValue();

      // Notify configs: flip back to "IsDisabled" semantics before sending.
      const updatedSystemConfigs = systemConfigs.reduce((acc: SystemConfig[], config) => {
        const newValue = formValues[config.key];
        const originalBool = parseBooleanValue(config.value);
        if (newValue !== originalBool) {
          acc.push({ key: config.key, value: String(!newValue) });
        }
        return acc;
      }, []);

      // Feature flags: enable semantics — send raw boolean.
      const updatedFeatureFlags = featureFlags.reduce((acc: SystemConfig[], config) => {
        const newValue = formValues[config.key];
        const originalBool = parseBooleanValue(config.value);
        if (newValue !== originalBool) {
          acc.push({ key: config.key, value: String(newValue) });
        }
        return acc;
      }, []);

      const requests: Promise<any>[] = [];
      if (updatedSystemConfigs.length > 0) {
        requests.push(
          apiUpdateSystemConfig({ systemConfigsList: updatedSystemConfigs, otpConfirm: otpValue })
        );
      }
      if (updatedFeatureFlags.length > 0) {
        requests.push(
          apiUpdateFeatureFlag({ featureFlagsList: updatedFeatureFlags, otpConfirm: otpValue })
        );
      }

      if (requests.length === 0) {
        toastError('Không có thay đổi để lưu');
        return;
      }

      await Promise.all(requests);
      toastSuccess('Cập nhật cấu hình thành công');
      setOtpModalVisible(false);
      setOtpValue('');
      setHasChanges(false);
      fetchAll();
    } catch (error: any) {
      console.error('Error updating system config:', error);
      const errorMessage = error?.response?.data?.message || 'Cập nhật cấu hình thất bại';
      toastError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderCheckbox = (config: SystemConfig, label: string) => (
    <Form.Item name={config.key} valuePropName="checked" style={{ marginBottom: 0 }}>
      <Checkbox>{label}</Checkbox>
    </Form.Item>
  );

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  const hasAny = systemConfigs.length > 0 || featureFlags.length > 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Quản lý cấu hình hệ thống
        </Title>
        <Space>
          <Button icon={<UndoOutlined />} onClick={handleCancelEdit} disabled={!hasChanges}>
            Hủy
          </Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveClick} disabled={!hasChanges}>
            Lưu thay đổi
          </Button>
        </Space>
      </div>

      {!hasAny ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Typography.Text type="secondary">Không có cấu hình hệ thống nào</Typography.Text>
          </div>
        </Card>
      ) : (
        <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
          {systemConfigs.length > 0 && (
            <>
              <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>Thông báo</Title>
              <Row gutter={[16, 16]}>
                {systemConfigs.map((config) => (
                  <Col xs={24} sm={12} lg={8} key={config.key}>
                    {renderCheckbox(config, getConfigDisplayName(config.key))}
                  </Col>
                ))}
              </Row>
            </>
          )}

          {featureFlags.length > 0 && (
            <>
              {systemConfigs.length > 0 && <Divider />}
              <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>Cấu hình tính năng</Title>
              <Row gutter={[16, 16]}>
                {featureFlags.map((config) => (
                  <Col xs={24} sm={12} lg={8} key={config.key}>
                    {renderCheckbox(config, getFeatureFlagDisplayName(config.key))}
                  </Col>
                ))}
              </Row>
            </>
          )}
        </Form>
      )}

      <Modal
        title="Xác nhận cập nhật cấu hình"
        open={otpModalVisible}
        onOk={handleSubmitUpdate}
        onCancel={() => {
          setOtpModalVisible(false);
          setOtpValue('');
        }}
        okText="Xác nhận"
        cancelText="Hủy"
      >
        <Divider />
        <p>
          <span style={{ color: 'red' }}>*</span> Nhập mã OTP xác nhận để cập nhật cấu hình hệ thống
        </p>
        <Input
          placeholder="Nhập mã OTP"
          value={otpValue}
          onChange={(e) => setOtpValue(e.target.value)}
          onPressEnter={handleSubmitUpdate}
        />
      </Modal>
    </div>
  );
}
